import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Paper,
  Chip,
  Stack,
  Checkbox,
  Tooltip,
  CircularProgress,
  IconButton,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ArticleIcon from '@mui/icons-material/Article';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';

import protocolUsdmCatalogueService from '../../services/protocolUsdmCatalogue.service';
import { normalizeUsdmCatalogue, getUsdmDataCoverage } from '../../protocol/utils/usdmCatalogueContext';
import { TEMPLATE_INTAKE_FIELD_META } from '../../data/templateIntakeCheckboxFieldMap';
import {
  generateUsdmTemplateField,
  UNSUPPORTED_USDM_TEMPLATE_FIELDS,
} from '../../protocol/utils/usdmTemplateFieldGeneration';
import CptTemplateTextField from './CptTemplateTextField';
import PatientLibrarySection54Panel from './PatientLibrarySection54Panel';
import { DEFAULT_PATIENT_LIBRARY_DECISIONS } from '../../utils/patientLibrarySection54';
import { inferPatientLibraryDecisionsFromUsdm } from '../../protocol/utils/patientLibrarySection54UsdmInference';

// Synthetic key (not a real TEMPLATE_INTAKE_FIELD_META field) used to persist
// the raw Patient Library decision-tree answers inside the same flat
// templateFieldValues map ProtocolUsdmCatalogue already exposes — avoids a
// backend schema change for storing this one structured sub-object.
const PATIENT_LIBRARY_DECISIONS_KEY = '__patient_library_decisions';
const PATIENT_LIBRARY_DECISIONS_PATH = 'template_language_fields.patient_library_decisions.';

// TEMPLATE_INTAKE_FIELD_META's own key order is historical/insertion order,
// not document order (e.g. every 11.x field is listed before 5.4.1/7.1/9.1) —
// sort by leading section number (component-wise, so "11.10" correctly sorts
// after "11.9" rather than before "11.2") so the field list below reads in
// the same sequence as the actual protocol sections.
function sectionNumberParts(fieldName) {
  const label = TEMPLATE_INTAKE_FIELD_META[fieldName]?.sectionLabel || '';
  const match = String(label).match(/^(\d+(?:\.\d+)*)/);
  return (match ? match[1] : '').split('.').map(Number);
}

function compareBySectionNumber(a, b) {
  const partsA = sectionNumberParts(a);
  const partsB = sectionNumberParts(b);
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const diff = (partsA[i] || 0) - (partsB[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

const TEMPLATE_FIELD_NAMES = Object.keys(TEMPLATE_INTAKE_FIELD_META).sort(compareBySectionNumber);

// Study Design's platform keeps a full version history but only ever has one
// version PUBLISHED at a time (a new publish moves the previous one to
// SUPERSEDED) — shown here so picking an older entry isn't a silent no-op;
// whether it's actually still downloadable is up to the platform's /download
// endpoint (surfaced as an error if it isn't).
function versionStatusLabel(status) {
  if (status === 'SUPERSEDED') return 'Superseded';
  if (status === 'RETRACTED') return 'Retracted';
  return status || '';
}

/**
 * Dedicated "USDM Template" sub-tab (Protocol section, alongside Protocol
 * Intake Form / Document Editor):
 *
 * 1. Upload a USDM catalogue JSON for this study.
 * 2. Per-field "Use Template" checkboxes — the USDM counterpart of
 *    ClinicalIntakeForm.jsx's inline per-field template checkboxes
 *    (templateIntakeCheckboxFieldMap.js's ~29 fields, same list, same base
 *    template text). Checking one tailors that field's template text using
 *    this study's USDM facts instead of clinical intake data
 *    (usdmTemplateFieldGeneration.js) and saves the result. 2 fields (7.1.2,
 *    7.1.3) stay disabled — they require Clinical Intake decision-tree
 *    answers with no USDM equivalent at all (UNSUPPORTED_USDM_TEMPLATE_FIELDS).
 *    The other 2 no-USDM-equivalent fields, 5.4.1/5.4.2 (Patient Library),
 *    get their decision-tree answers from a dedicated AI classification step
 *    instead (patientLibrarySection54UsdmInference.js), run as part of
 *    "Apply Templates" below.
 * 3. Generate the full template via the "Generate Protocol" button below —
 *    it pulls in whatever's saved here per field, and (unconditionally,
 *    matching Clinical Intake-driven generation) whatever Template Language
 *    rows are checked on the Template Language tab. Navigates to the
 *    Document Editor tab and runs the exact same generation that used to
 *    live on that tab's own "Generate from USDM" toolbar button (moved here,
 *    not duplicated — see DocumentEditor.jsx's autoGenerateFromUsdm effect).
 */
export default function UsdmTemplateTab({ projectId, onViewInEditor, onGenerateProtocol }) {
  const fileInputRef = useRef(null);

  const [catalogueStatus, setCatalogueStatus] = useState({ loading: true, uploaded: false });
  const [catalogueMeta, setCatalogueMeta] = useState(null); // { sourceVersionLabel, sourceIsLatestVersion, ... }
  const [uploading, setUploading] = useState(false);
  const [fetchingFromStudyDesign, setFetchingFromStudyDesign] = useState(false);
  const [studyDesignVersions, setStudyDesignVersions] = useState([]); // [{ versionId, versionLabel, isLatest, ... }]
  const [selectedVersionId, setSelectedVersionId] = useState(''); // '' = latest
  const [versionsRefreshing, setVersionsRefreshing] = useState(false);
  const [applyingTemplates, setApplyingTemplates] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null); // { severity, text }

  const [usdmFacts, setUsdmFacts] = useState(null);
  const [factsError, setFactsError] = useState('');
  // What the loaded export contains vs lacks, per area (objectives, eligibility, sample size, ...).
  const usdmCoverage = useMemo(() => {
    try {
      return getUsdmDataCoverage(usdmFacts);
    } catch (err) {
      console.error('UsdmTemplateTab: failed to build data coverage summary:', err);
      return [];
    }
  }, [usdmFacts]);
  const usdmMissingCount = usdmCoverage.filter((row) => row.status === 'missing').length;
  const [fieldSelections, setFieldSelections] = useState({}); // { [fieldName]: boolean }
  const [fieldValues, setFieldValues] = useState({}); // { [fieldName]: string }
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldBusy, setFieldBusy] = useState({}); // { [fieldName]: boolean }
  const [fieldError, setFieldError] = useState({}); // { [fieldName]: string }
  const [fieldDirty, setFieldDirty] = useState({}); // { [fieldName]: boolean } — edited locally, not yet saved
  const [fieldSaving, setFieldSaving] = useState({}); // { [fieldName]: boolean }
  const [patientLibraryDecisions, setPatientLibraryDecisions] = useState(DEFAULT_PATIENT_LIBRARY_DECISIONS);
  const [inferringPatientLibrary, setInferringPatientLibrary] = useState(false);
  const [patientLibraryInferError, setPatientLibraryInferError] = useState('');

  const refreshCatalogueStatus = useCallback(async () => {
    if (!projectId) {
      setCatalogueStatus({ loading: false, uploaded: false });
      setUsdmFacts(null);
      setCatalogueMeta(null);
      return;
    }
    setCatalogueStatus((prev) => ({ ...prev, loading: true }));
    try {
      const meta = await protocolUsdmCatalogueService.getLatestCatalogueMeta(projectId);
      setCatalogueMeta(meta || null);
      setCatalogueStatus({ loading: false, uploaded: Boolean(meta?.data) });
      // Reading the facts is separate from "is a catalogue loaded": a parsing
      // failure must surface as an error, not make the catalogue look absent.
      try {
        setUsdmFacts(meta?.data ? normalizeUsdmCatalogue(meta.data) : null);
        setFactsError('');
      } catch (factsErr) {
        console.error('UsdmTemplateTab: failed to read USDM catalogue facts:', factsErr);
        setUsdmFacts(null);
        setFactsError(factsErr?.message || String(factsErr));
      }
    } catch (err) {
      setCatalogueStatus({ loading: false, uploaded: false });
      setUsdmFacts(null);
      setCatalogueMeta(null);
    }
  }, [projectId]);

  // Populates the "fetch by version" picker — defaults to whatever the
  // platform flags isLatest so "Fetch from Study Design Studio" keeps its
  // original always-latest behavior until the user deliberately picks
  // something else. Only ever fetched once on mount previously — a version
  // published in Study Design Studio *after* this tab loaded (or after the
  // last fetch) never showed up until a full page reload, so the picker kept
  // labeling a now-superseded version "Latest". Now also re-run after a
  // fetch and from a manual refresh button, and re-runs use a functional
  // setState read of the current selection so a deliberately-picked older
  // version survives the refresh instead of always snapping back to latest.
  const refreshStudyDesignVersions = useCallback(async () => {
    if (!projectId) {
      setStudyDesignVersions([]);
      setSelectedVersionId('');
      return;
    }
    setVersionsRefreshing(true);
    try {
      const versions = await protocolUsdmCatalogueService.listStudyDesignVersions(projectId);
      setStudyDesignVersions(versions);
      setSelectedVersionId((prevSelected) => {
        if (prevSelected && versions.some((v) => v.versionId === prevSelected)) return prevSelected;
        const latest = versions.find((v) => v.isLatest) || versions[0];
        return latest?.versionId || '';
      });
    } finally {
      setVersionsRefreshing(false);
    }
  }, [projectId]);

  const refreshFieldState = useCallback(async () => {
    if (!projectId) {
      setFieldSelections({});
      setFieldValues({});
      setPatientLibraryDecisions(DEFAULT_PATIENT_LIBRARY_DECISIONS);
      setFieldsLoading(false);
      return;
    }
    setFieldsLoading(true);
    const { templateFieldSelections, templateFieldValues } =
      await protocolUsdmCatalogueService.getTemplateFieldState(projectId);
    setFieldSelections(templateFieldSelections || {});
    setFieldValues(templateFieldValues || {});
    const storedDecisions = templateFieldValues?.[PATIENT_LIBRARY_DECISIONS_KEY];
    if (storedDecisions) {
      try {
        setPatientLibraryDecisions({ ...DEFAULT_PATIENT_LIBRARY_DECISIONS, ...JSON.parse(storedDecisions) });
      } catch (e) {
        setPatientLibraryDecisions(DEFAULT_PATIENT_LIBRARY_DECISIONS);
      }
    } else {
      setPatientLibraryDecisions(DEFAULT_PATIENT_LIBRARY_DECISIONS);
    }
    setFieldsLoading(false);
  }, [projectId]);

  useEffect(() => {
    refreshCatalogueStatus();
    refreshFieldState();
    refreshStudyDesignVersions();
  }, [refreshCatalogueStatus, refreshFieldState, refreshStudyDesignVersions]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !projectId) return;
    setUploading(true);
    setUploadMessage(null);
    try {
      const result = await protocolUsdmCatalogueService.uploadCatalogue(projectId, file);
      const hasWarnings = Array.isArray(result?.warnings) && result.warnings.length > 0;
      setUploadMessage({
        severity: hasWarnings ? 'warning' : 'success',
        text: hasWarnings
          ? `Uploaded successfully with warnings: ${result.warnings.join(' ')}`
          : `Uploaded successfully — ${result?.classCount ?? 0} classes, ${result?.instanceCount ?? 0} instances.`,
      });
      await refreshCatalogueStatus();
      // A fresh upload is a new ProtocolUsdmCatalogue doc with its own blank
      // per-field state — reload so the checkboxes reflect that.
      await refreshFieldState();
    } catch (err) {
      setUploadMessage({ severity: 'error', text: err.message || 'Failed to upload USDM catalogue' });
    } finally {
      setUploading(false);
    }
  };

  const handleFetchFromStudyDesign = async () => {
    if (!projectId) return;
    const wasAlreadyUploaded = catalogueStatus.uploaded;
    setFetchingFromStudyDesign(true);
    setUploadMessage(null);
    try {
      const result = await protocolUsdmCatalogueService.fetchFromStudyDesign(projectId, selectedVersionId || null);
      const hasWarnings = Array.isArray(result?.warnings) && result.warnings.length > 0;
      const verb = wasAlreadyUploaded ? 'updated' : 'fetched';
      const versionSuffix = result?.versionLabel
        ? ` (v${result.versionLabel}${result.isLatestVersion ? ', latest' : ''})`
        : '';
      setUploadMessage({
        severity: hasWarnings ? 'warning' : 'success',
        text: hasWarnings
          ? `Catalogue ${verb} from Study Design${versionSuffix} with warnings: ${result.warnings.join(' ')}`
          : `Catalogue ${verb} from Study Design${versionSuffix} — ${result?.classCount ?? 0} classes, ${result?.instanceCount ?? 0} instances.`,
      });
      await refreshCatalogueStatus();
      // Same reasoning as handleUpload — a fresh catalogue doc has its own
      // blank per-field state, so reload so the checkboxes reflect that.
      await refreshFieldState();
      // Re-check the platform's version list too — a version published there
      // between this tab loading and this fetch completing wouldn't
      // otherwise show up (or get its "Latest" label corrected) until a full
      // page reload. See refreshStudyDesignVersions' comment.
      await refreshStudyDesignVersions();
    } catch (err) {
      setUploadMessage({ severity: 'error', text: err.message || 'Failed to fetch USDM catalogue from Study Design' });
    } finally {
      setFetchingFromStudyDesign(false);
    }
  };

  const runFieldGeneration = async (fieldName) => {
    setFieldError((prev) => ({ ...prev, [fieldName]: '' }));
    setFieldBusy((prev) => ({ ...prev, [fieldName]: true }));
    try {
      const result = await generateUsdmTemplateField(fieldName, usdmFacts);
      if (!result.ok) {
        setFieldError((prev) => ({ ...prev, [fieldName]: result.message }));
        setFieldSelections((prev) => ({ ...prev, [fieldName]: false }));
        return false;
      }
      setFieldValues((prev) => ({ ...prev, [fieldName]: result.finalText }));
      setFieldDirty((prev) => ({ ...prev, [fieldName]: false }));
      await protocolUsdmCatalogueService.updateTemplateFieldState(projectId, fieldName, true, result.finalText);
      return true;
    } catch (err) {
      setFieldError((prev) => ({ ...prev, [fieldName]: err.message || 'Failed to generate template text' }));
      setFieldSelections((prev) => ({ ...prev, [fieldName]: false }));
      return false;
    } finally {
      setFieldBusy((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleFieldToggle = async (fieldName, checked) => {
    setFieldSelections((prev) => ({ ...prev, [fieldName]: checked }));
    if (!checked) {
      setFieldValues((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
      setFieldError((prev) => ({ ...prev, [fieldName]: '' }));
      setFieldDirty((prev) => ({ ...prev, [fieldName]: false }));
      try {
        await protocolUsdmCatalogueService.updateTemplateFieldState(projectId, fieldName, false, '');
      } catch (err) {
        // Non-fatal — local state is already unchecked; next load will just
        // re-fetch whatever the server actually has.
      }
      return true;
    }
    return runFieldGeneration(fieldName);
  };

  // Bulk counterpart of checking every "Use Template" box one at a time —
  // generates (or regenerates) every field EXCEPT the ones with no USDM
  // equivalent at all (UNSUPPORTED_USDM_TEMPLATE_FIELDS: 7.1.2, 7.1.3, which
  // require Protocol Intake Form decision-tree answers no USDM data can
  // answer). 5.4.1/5.4.2 ARE included here — via runPatientLibraryAutoSelect,
  // run alongside the other fields — since those two now have an AI-assisted
  // decision-tree answer path (patientLibrarySection54UsdmInference.js).
  // Runs concurrently — each field already has its own busy spinner/error
  // display in the list below, so progress is visible per-field without a
  // separate progress UI.
  const handleApplyTemplates = async () => {
    if (!projectId) return;
    const eligibleFields = TEMPLATE_FIELD_NAMES.filter((f) => !UNSUPPORTED_USDM_TEMPLATE_FIELDS[f]);
    setApplyingTemplates(true);
    setUploadMessage(null);
    try {
      const [fieldResults, patientLibraryOk] = await Promise.all([
        Promise.all(eligibleFields.map((fieldName) => handleFieldToggle(fieldName, true))),
        runPatientLibraryAutoSelect(),
      ]);
      const totalCount = eligibleFields.length + 1;
      const failedCount = fieldResults.filter((ok) => !ok).length + (patientLibraryOk ? 0 : 1);
      setUploadMessage(
        failedCount > 0
          ? {
              severity: 'warning',
              text: `Templates applied — ${totalCount - failedCount} of ${totalCount} generated successfully (${failedCount} failed${patientLibraryOk ? '' : ', including Patient Library 5.4.1/5.4.2'}; see individual fields below for details).`,
            }
          : {
              severity: 'success',
              text: `Templates applied — ${totalCount} fields generated, including Patient Library 5.4.1/5.4.2 (skipped 7.1.2, 7.1.3 — no USDM equivalent).`,
            }
      );
    } finally {
      setApplyingTemplates(false);
    }
  };

  // Persists the raw Patient Library decision-tree answers (needed to
  // reconstruct the panel's radio selections on reload) under a synthetic
  // key in the same flat templateFieldValues map every other field uses.
  const persistPatientLibraryDecisions = useCallback(
    (decisions) => {
      if (!projectId) return;
      protocolUsdmCatalogueService
        .updateTemplateFieldState(projectId, PATIENT_LIBRARY_DECISIONS_KEY, true, JSON.stringify(decisions))
        .catch(() => {
          // Non-fatal — local state already reflects the change; next load
          // will just re-fetch whatever the server actually has.
        });
    },
    [projectId]
  );

  // Persists one of the two verbatim-assembled fields (definitions_childbearing_potential /
  // contraception_requirements) into the SAME storage every LLM-generated field uses, so
  // DocumentEditor's "Generate from USDM" (resolveUsdmSectionForField) picks it up identically —
  // no special-casing needed on the generation side.
  const persistAssembledPatientLibraryField = useCallback(
    (fieldName, text) => {
      const checked = Boolean(text && String(text).trim());
      setFieldValues((prev) => ({ ...prev, [fieldName]: text || '' }));
      setFieldSelections((prev) => ({ ...prev, [fieldName]: checked }));
      if (!projectId) return;
      protocolUsdmCatalogueService
        .updateTemplateFieldState(projectId, fieldName, checked, text || '')
        .catch(() => {
          // Non-fatal — same reasoning as above.
        });
    },
    [projectId]
  );

  // Adapter satisfying PatientLibrarySection54Panel's Formik-shaped
  // `setFieldValue(path, value)` contract (the same component ClinicalIntakeForm.jsx
  // uses for this exact decision tree — see its own file header) against this tab's
  // plain useState model. The panel only ever writes to these three path shapes.
  const handlePatientLibraryFieldValue = useCallback(
    (path, value) => {
      if (path.startsWith(PATIENT_LIBRARY_DECISIONS_PATH)) {
        const key = path.slice(PATIENT_LIBRARY_DECISIONS_PATH.length);
        setPatientLibraryDecisions((prev) => {
          const next = { ...prev, [key]: value };
          persistPatientLibraryDecisions(next);
          return next;
        });
        return;
      }
      if (
        path === 'template_language_fields.definitions_childbearing_potential' ||
        path === 'template_language_fields.contraception_requirements'
      ) {
        persistAssembledPatientLibraryField(path.slice('template_language_fields.'.length), value);
      }
    },
    [persistPatientLibraryDecisions, persistAssembledPatientLibraryField]
  );

  // AI-assisted answer selection for the Section 5.4.1/5.4.2 decision tree
  // (patientLibrarySection54UsdmInference.js). Only picks the decision-tree
  // answers from the uploaded USDM catalogue — applying them here just
  // updates `patientLibraryDecisions` exactly like a manual radio click
  // would, so PatientLibrarySection54Panel's own effect still deterministically
  // assembles and persists the verbatim 5.4.1/5.4.2 text via
  // handlePatientLibraryFieldValue above. No separate confirmation step.
  // Run as part of handleApplyTemplates (bulk run); there is no separate
  // button for it, since Apply Templates already covers it.
  const runPatientLibraryAutoSelect = useCallback(async () => {
    if (!usdmFacts) return false;
    setInferringPatientLibrary(true);
    setPatientLibraryInferError('');
    try {
      const result = await inferPatientLibraryDecisionsFromUsdm(usdmFacts);
      if (!result.ok) {
        setPatientLibraryInferError(result.message);
        return false;
      }
      setPatientLibraryDecisions(result.decisions);
      persistPatientLibraryDecisions(result.decisions);
      return true;
    } finally {
      setInferringPatientLibrary(false);
    }
  }, [usdmFacts, persistPatientLibraryDecisions]);

  const patientLibraryFormValues = useMemo(
    () => ({
      template_language_fields: { patient_library_decisions: patientLibraryDecisions },
      target_population: { reproductive_status_contraception: '' },
    }),
    [patientLibraryDecisions]
  );

  // CptTemplateTextField commits an edit (plain-text edit, or the plain
  // fallback TextField for non-CPT fields) via field.onChange — that only
  // updates local state, same as a Formik field before the form is saved.
  // Nothing is persisted until the explicit Save button below is clicked.
  const handleFieldValueEdit = (fieldName, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    setFieldDirty((prev) => ({ ...prev, [fieldName]: true }));
  };

  const handleSaveFieldEdit = async (fieldName) => {
    setFieldSaving((prev) => ({ ...prev, [fieldName]: true }));
    setFieldError((prev) => ({ ...prev, [fieldName]: '' }));
    try {
      const value = fieldValues[fieldName] || '';
      await protocolUsdmCatalogueService.updateTemplateFieldState(projectId, fieldName, true, value);
      setFieldSelections((prev) => ({ ...prev, [fieldName]: true }));
      setFieldDirty((prev) => ({ ...prev, [fieldName]: false }));
    } catch (err) {
      setFieldError((prev) => ({ ...prev, [fieldName]: err.message || 'Failed to save edit' }));
    } finally {
      setFieldSaving((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const disabledReason = !projectId
    ? 'Open a study first.'
    : !catalogueStatus.uploaded
      ? 'Upload a USDM catalogue first.'
      : '';

  // "Generate Protocol" stays disabled until template generation has actually
  // produced something to generate from — i.e. at least one eligible field
  // (excluding the 4 with no USDM equivalent) has been checked AND has text,
  // either from "Apply Templates" just finishing or from a prior session's
  // already-saved state (reloaded via refreshFieldState on mount). Deriving
  // this from real field state — rather than a one-off "did Apply Templates
  // run this session" flag — means it stays correctly enabled after a page
  // reload when templates were already generated earlier, and correctly
  // resets to disabled after fetching/uploading a new catalogue (which always
  // starts that catalogue's field state blank).
  const hasGeneratedTemplates = TEMPLATE_FIELD_NAMES.some(
    (f) => !UNSUPPORTED_USDM_TEMPLATE_FIELDS[f] && fieldSelections[f] && fieldValues[f]
  );

  return (
    <Box sx={{ py: 3, px: 2, minHeight: '60vh' }}>
      <Typography variant="h6" gutterBottom>USDM Template</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Fetch a USDM catalogue for this study, choose which template fields should be tailored from it,
        then generate the full protocol with the "Generate Protocol" button below.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>1. USDM catalogue</Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2, rowGap: 1 }}>
          {studyDesignVersions.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="usdm-study-design-version-label">Version</InputLabel>
              <Select
                labelId="usdm-study-design-version-label"
                label="Version"
                value={selectedVersionId}
                onChange={(e) => setSelectedVersionId(e.target.value)}
                disabled={applyingTemplates || uploading || fetchingFromStudyDesign}
              >
                {studyDesignVersions.map((v) => (
                  <MenuItem key={v.versionId} value={v.versionId}>
                    v{v.versionLabel}
                    {v.isLatest ? ' — Latest' : v.status && v.status !== 'PUBLISHED' ? ` — ${versionStatusLabel(v.status)}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Re-check Study Design Studio for newer published versions">
            <span>
              <IconButton
                size="small"
                onClick={refreshStudyDesignVersions}
                disabled={applyingTemplates || uploading || fetchingFromStudyDesign || versionsRefreshing || !projectId}
              >
                {versionsRefreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            startIcon={<CloudSyncIcon />}
            variant="outlined"
            onClick={handleFetchFromStudyDesign}
            disabled={applyingTemplates || uploading || fetchingFromStudyDesign || !projectId}
          >
            {fetchingFromStudyDesign ? 'Fetching…' : 'Fetch from Study Design Studio'}
          </Button>
          <Button
            startIcon={<PlaylistAddCheckIcon />}
            variant="outlined"
            onClick={handleApplyTemplates}
            disabled={applyingTemplates || uploading || fetchingFromStudyDesign || !projectId || !catalogueStatus.uploaded}
          >
            {applyingTemplates ? 'Applying…' : 'Apply Templates'}
          </Button>
          <Tooltip
            title={
              disabledReason
                ? disabledReason
                : !hasGeneratedTemplates
                  ? 'Run Apply Templates first — Generate Protocol needs at least one generated template field.'
                  : ''
            }
          >
            <span>
              <Button
                startIcon={<PlayArrowIcon />}
                variant="contained"
                onClick={() => onGenerateProtocol && onGenerateProtocol()}
                disabled={
                  Boolean(disabledReason) ||
                  uploading ||
                  fetchingFromStudyDesign ||
                  applyingTemplates ||
                  !onGenerateProtocol ||
                  !hasGeneratedTemplates
                }
              >
                Generate Protocol
              </Button>
            </span>
          </Tooltip>
          {!catalogueStatus.loading && (
            <Chip
              size="small"
              color={catalogueStatus.uploaded ? 'success' : 'default'}
              label={
                catalogueStatus.uploaded
                  ? catalogueMeta?.sourceVersionLabel
                    ? `Catalogue uploaded (v${catalogueMeta.sourceVersionLabel}${catalogueMeta.sourceIsLatestVersion ? ', latest' : ''})`
                    : 'Catalogue uploaded'
                  : 'No catalogue uploaded yet'
              }
            />
          )}
        </Stack>
        {uploadMessage && (
          <Alert severity={uploadMessage.severity} sx={{ mb: 1 }} onClose={() => setUploadMessage(null)}>
            {uploadMessage.text}
          </Alert>
        )}
        {catalogueStatus.uploaded && factsError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            The catalogue is loaded, but its study design data could not be read: {factsError}
          </Alert>
        )}
        {catalogueStatus.uploaded && !factsError && usdmFacts && usdmCoverage.length === 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            The catalogue is loaded, but the data summary could not be built. See the browser console for details.
          </Alert>
        )}
        {catalogueStatus.uploaded && usdmCoverage.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Study design data in this catalogue
              {catalogueMeta?.sourceVersionLabel ? ` (v${catalogueMeta.sourceVersionLabel})` : ''}
            </Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td': { py: 0.75, pr: 2, verticalAlign: 'top', borderBottom: '1px solid', borderColor: 'divider' } }}>
              <tbody>
                {usdmCoverage.map((row) => (
                  <tr key={row.key}>
                    <td style={{ width: '28%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.label}</Typography>
                    </td>
                    <td style={{ width: '14%' }}>
                      <Chip
                        size="small"
                        label={row.status === 'present' ? 'Present' : row.status === 'provisional' ? 'Provisional only' : 'Missing'}
                        color={row.status === 'present' ? 'success' : row.status === 'provisional' ? 'warning' : 'error'}
                        variant={row.status === 'present' ? 'filled' : 'outlined'}
                      />
                    </td>
                    <td>
                      <Typography variant="body2">{row.detail}</Typography>
                      {row.openParameters.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Open in Study Design Studio: {row.openParameters.join(', ')}
                        </Typography>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
            {usdmMissingCount > 0 && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                The generated protocol will state &quot;Not yet defined in Study Design&quot; for missing items and
                label provisional items as provisional. Complete them in Study Design Studio, publish, then fetch again.
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 0 }}>2. Patient Library — Sections 5.4.1 &amp; 5.4.2</Typography>
          {inferringPatientLibrary && <CircularProgress size={16} />}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Answer the same decision tree used in the Protocol Intake Form, either by hand below or via
          &ldquo;Apply Templates&rdquo; above (AI picks the decision-tree answers from this study&rsquo;s
          uploaded USDM facts — the Section 5.4.1/5.4.2 text itself is always assembled verbatim from the
          Patient Library, never written by AI). The result is saved automatically and is picked up by
          &ldquo;Generate Protocol&rdquo; the same way as every other checked field below.
        </Typography>
        {disabledReason && <Alert severity="info" sx={{ mb: 2 }}>{disabledReason}</Alert>}
        {patientLibraryInferError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPatientLibraryInferError('')}>
            {patientLibraryInferError}
          </Alert>
        )}
        <PatientLibrarySection54Panel
          values={patientLibraryFormValues}
          setFieldValue={handlePatientLibraryFieldValue}
          isFormDisabled={Boolean(disabledReason) || fetchingFromStudyDesign || applyingTemplates || inferringPatientLibrary}
        />
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="5.4.1 Definitions Related to Childbearing Potential"
            value={fieldValues.definitions_childbearing_potential || ''}
            placeholder="Answer Section 5.4.1 above to assemble this text."
            fullWidth
            multiline
            minRows={4}
            disabled
          />
          <TextField
            label="5.4.2 Contraception Requirements"
            value={fieldValues.contraception_requirements || ''}
            placeholder="Answer Section 5.4.2 above to assemble this text."
            fullWidth
            multiline
            minRows={4}
            disabled
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>3. Template fields</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Same field list as the Protocol Intake Form's per-field "Use Template" checkboxes. Checking one
          tailors that field's standard template text using this study's uploaded USDM data (not clinical
          intake data) and saves the result. Click the refresh icon on a checked field to regenerate it.
        </Typography>
        {disabledReason && <Alert severity="info" sx={{ mb: 2 }}>{disabledReason}</Alert>}
        {fieldsLoading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : (
          TEMPLATE_FIELD_NAMES.map((fieldName, idx) => {
            const meta = TEMPLATE_INTAKE_FIELD_META[fieldName];
            const unsupportedReason = UNSUPPORTED_USDM_TEMPLATE_FIELDS[fieldName];
            const checked = Boolean(fieldSelections[fieldName]);
            const busy = Boolean(fieldBusy[fieldName]);
            const saving = Boolean(fieldSaving[fieldName]);
            const dirty = Boolean(fieldDirty[fieldName]);
            const error = fieldError[fieldName];
            const isDisabled = Boolean(unsupportedReason) || Boolean(disabledReason) || busy || applyingTemplates;
            const showEditor = !unsupportedReason && (checked || fieldValues[fieldName]);

            return (
              <Box key={fieldName}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Checkbox
                    checked={checked}
                    onChange={(e) => handleFieldToggle(fieldName, e.target.checked)}
                    disabled={isDisabled}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{meta.sectionLabel}</Typography>
                  {busy && <CircularProgress size={14} />}
                  {!busy && checked && fieldValues[fieldName] && (
                    <Tooltip title="Regenerate from the current USDM catalogue (discards edits)">
                      <IconButton size="small" onClick={() => runFieldGeneration(fieldName)} disabled={isDisabled}>
                        <RefreshIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {unsupportedReason && (
                    <Tooltip title={unsupportedReason}>
                      <InfoOutlinedIcon fontSize="small" color="disabled" />
                    </Tooltip>
                  )}
                  {error && (
                    <Tooltip title={error}>
                      <InfoOutlinedIcon fontSize="small" color="error" />
                    </Tooltip>
                  )}
                </Stack>

                {showEditor && (
                  <Box sx={{ pl: 4.5, pb: 1 }}>
                    <CptTemplateTextField
                      field={{
                        name: fieldName,
                        value: fieldValues[fieldName] || '',
                        onChange: (e) => handleFieldValueEdit(fieldName, e.target.value),
                        onBlur: () => {},
                      }}
                      disabled={busy}
                      placeholder={busy ? 'Generating…' : ''}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SaveIcon fontSize="small" />}
                      onClick={() => handleSaveFieldEdit(fieldName)}
                      disabled={!dirty || busy || saving}
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </Box>
                )}

                {idx < TEMPLATE_FIELD_NAMES.length - 1 && <Divider sx={{ my: 1.5 }} />}
              </Box>
            );
          })
        )}
      </Paper>

      <Button
        startIcon={<ArticleIcon />}
        variant="text"
        onClick={() => onViewInEditor && onViewInEditor()}
      >
        Go to Document Editor
      </Button>
    </Box>
  );
}
